import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Lock, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  api,
  type ContainerDetailOut,
  type ContainerLineOut,
  type MarketplaceOut,
} from "@/lib/api";
import { formatDate, formatDecimal, formatPercent, formatPrice } from "@/lib/format";

/**
 * Dettaglio di un container: costi, righe, margine per canale, push del prezzo.
 *
 * Le colonne di margine per canale sono la ragione della pagina: lo stesso
 * prezzo rende diversamente dove la commissione e' diversa, e vederle
 * affiancate e' cio' che fa scegliere il prezzo. Un "margine" al singolare non
 * direbbe niente.
 *
 * Il push riusa `POST /reprice/manual`, l'unica scrittura che il lucchetto
 * lascia passare: e' cosi' che il prezzo lo decide una persona.
 */

const ATTENZIONE: Record<string, string> = {
  sbarco_passato: "Sbarco passato: dichiaralo a terra",
  prezzo_mancante: "Righe senza prezzo deciso",
  fermo_da_troppo: "Fermo da oltre 6 mesi",
  nessuna_riga: "Nessuna riga",
};

function Stat({ k, v, accent }: { k: string; v: string; accent?: boolean }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 bg-card px-3.5 py-2.5">
      <span className="text-label text-ink-3 first-letter:uppercase">{k}</span>
      <span className={accent ? "font-mono text-xl tabular-nums text-accent" : "font-mono text-xl tabular-nums"}>
        {v}
      </span>
    </div>
  );
}

function marginTone(pct: string | null): string | undefined {
  if (pct === null) return undefined;
  const n = Number(pct);
  if (Number.isNaN(n)) return undefined;
  // Il colore accompagna il numero, non lo sostituisce: la percentuale resta
  // sempre leggibile (indipendenza dal colore, WCAG).
  if (n < 0) return "hsl(var(--danger))";
  if (n < 10) return "hsl(var(--warn))";
  return undefined;
}

export function ContainerDetail(): JSX.Element {
  const { code = "" } = useParams();
  const qc = useQueryClient();
  const container = useQuery({
    queryKey: ["container", code],
    queryFn: () => api.container(code),
    enabled: code !== "",
  });
  const marketplaces = useQuery({ queryKey: ["marketplaces"], queryFn: api.marketplaces });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [prezzo, setPrezzo] = useState("");
  const [canali, setCanali] = useState<Set<string>>(new Set());
  const [esito, setEsito] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const detail = container.data;
  const lines = useMemo(() => detail?.lines ?? [], [detail]);

  // I canali con almeno un margine calcolato: sono quelli su cui questi
  // articoli vendono davvero, non tutto il registro marketplace.
  const canaliAttivi = useMemo(() => {
    const ids = new Set<string>();
    for (const line of lines) for (const id of Object.keys(line.margins)) ids.add(id);
    return [...ids].sort();
  }, [lines]);

  const labelDi = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of marketplaces.data ?? ([] as MarketplaceOut[])) map.set(m.id, m.label);
    return map;
  }, [marketplaces.data]);

  const headerCheckbox = useRef<HTMLInputElement>(null);
  const allSelected = lines.length > 0 && lines.every((l) => selected.has(l.id));
  const someSelected = selected.size > 0 && !allSelected;
  useEffect(() => {
    if (headerCheckbox.current) headerCheckbox.current.indeterminate = someSelected;
  }, [someSelected]);

  const land = useMutation({
    mutationFn: () => api.landContainer(code),
    onSuccess: (r) => {
      setErrore(null);
      const note = [
        `${r.applied.length} prodotti aggiornati`,
        r.missing_sku.length ? `${r.missing_sku.length} sku fuori catalogo` : null,
        r.no_cost.length ? `${r.no_cost.length} senza costo` : null,
      ].filter(Boolean);
      setEsito(`A terra: ${note.join(", ")}.`);
      qc.invalidateQueries({ queryKey: ["container", code] });
      qc.invalidateQueries({ queryKey: ["containers"] });
    },
    onError: (e: Error) => setErrore(e.message),
  });

  const close = useMutation({
    mutationFn: (successor: string | null) => api.closeContainer(code, successor),
    onSuccess: (r) => {
      setErrore(null);
      setEsito(
        `Chiuso: ${r.released.length} restituiti al fornitore, ` +
          `${r.handed_over.length} passati al container successivo` +
          (r.still_locked.length
            ? `, ${r.still_locked.length} lasciati bloccati (nessun costo a cui tornare)`
            : "") +
          ".",
      );
      qc.invalidateQueries({ queryKey: ["container", code] });
      qc.invalidateQueries({ queryKey: ["containers"] });
    },
    onError: (e: Error) => setErrore(e.message),
  });

  const push = useMutation({
    mutationFn: async () => {
      const righe = lines.filter((l) => selected.has(l.id));
      const esiti: string[] = [];
      for (const line of righe) {
        for (const marketplaceId of canali) {
          const r = await api.manualReprice({
            sku: line.sku,
            marketplace_id: marketplaceId,
            new_price: prezzo,
          });
          if (!r.poleepo.success && !r.mirakl.success) {
            esiti.push(`${line.sku}/${marketplaceId}: ${r.poleepo.error || r.mirakl.error}`);
          }
        }
      }
      return esiti;
    },
    onSuccess: (falliti) => {
      setErrore(null);
      const totale = selected.size * canali.size;
      setEsito(
        falliti.length === 0
          ? `${totale} offerte scritte a ${prezzo} €.`
          : `${totale - falliti.length}/${totale} scritte. Non riuscite: ${falliti.join("; ")}`,
      );
      qc.invalidateQueries({ queryKey: ["container", code] });
    },
    onError: (e: Error) => setErrore(e.message),
  });

  const removeLine = useMutation({
    mutationFn: (lineId: number) => api.deleteContainerLine(code, lineId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["container", code] }),
    onError: (e: Error) => setErrore(e.message),
  });

  function toggle(lineId: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }

  function toggleCanale(id: string): void {
    setCanali((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (container.isLoading) {
    return <p className="px-7 py-6 text-sm text-muted-foreground">Carico…</p>;
  }
  if (container.isError || !detail) {
    return (
      <div className="flex flex-col gap-3 px-7 py-6">
        <Link to="/container" className="text-sm underline underline-offset-2">
          ← Container
        </Link>
        <p role="alert" className="text-sm text-danger">
          Container non trovato.
        </p>
      </div>
    );
  }

  const pushAbilitato =
    selected.size > 0 && canali.size > 0 && prezzo.trim() !== "" && !push.isPending;

  return (
    <div className="flex flex-col gap-5 px-7 py-6">
      <Link
        to="/container"
        className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
        Container
      </Link>

      <Header detail={detail} />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-px overflow-hidden rounded-[var(--radius-lg)] border border-line bg-line">
        <Stat k="pezzi" v={String(detail.total_pieces)} />
        <Stat k="merce" v={formatPrice(detail.goods_value)} />
        <Stat k="logistica maturata" v={formatPrice(detail.logistics_accrued)} />
        <Stat k="mesi a magazzino" v={formatDecimal(detail.months_in_stock)} />
        <Stat k="righe" v={String(detail.line_count)} />
      </div>

      {detail.attention.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Da guardare">
          {detail.attention.map((a) => (
            <li
              key={a}
              className="pill pill-risk"
            >
              {ATTENZIONE[a] ?? a}
            </li>
          ))}
        </ul>
      ) : null}

      <Lines
        lines={lines}
        canaliAttivi={canaliAttivi}
        labelDi={labelDi}
        selected={selected}
        headerCheckbox={headerCheckbox}
        allSelected={allSelected}
        onToggle={toggle}
        onToggleAll={() =>
          setSelected(allSelected ? new Set() : new Set(lines.map((l) => l.id)))
        }
        onRemove={(id) => removeLine.mutate(id)}
      />

      {selected.size > 0 ? (
        <div
          className="selection-bar flex-wrap"
        >
          <span className="text-label font-semibold">
            {selected.size} righe selezionate
          </span>
          <label className="flex items-center gap-1.5">
            <span>Prezzo</span>
            <input
              value={prezzo}
              onChange={(e) => setPrezzo(e.target.value)}
              inputMode="decimal"
              placeholder="97,60"
              aria-label="Prezzo lordo da scrivere sui canali"
              className="h-8 w-24 rounded-md border border-white/30 bg-white/10 px-2 font-mono text-data text-white placeholder:text-rail-text"
            />
          </label>
          <fieldset className="flex flex-wrap items-center gap-2">
            <legend className="sr-only">Canali su cui scrivere</legend>
            {canaliAttivi.map((id) => (
              <label key={id} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={canali.has(id)}
                  onChange={() => toggleCanale(id)}
                />
                <span>{labelDi.get(id) ?? id}</span>
              </label>
            ))}
          </fieldset>
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="btn btn-sm border-white/30 bg-transparent text-white hover:bg-white/10"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={!pushAbilitato}
            onClick={() => push.mutate()}
            className="btn btn-sm btn-accent"
          >
            {push.isPending
              ? "Scrivo…"
              : `Spingi su ${selected.size * canali.size} offerte`}
          </button>
        </div>
      ) : null}

      <Actions detail={detail} onLand={() => land.mutate()} onClose={(s) => close.mutate(s)} />

      {esito ? (
        <p role="status" className="text-sm text-muted-foreground">
          {esito}
        </p>
      ) : null}
      {errore ? (
        <p role="alert" className="text-sm text-danger">
          {errore}
        </p>
      ) : null}
    </div>
  );
}

function Header({ detail }: { detail: ContainerDetailOut }): JSX.Element {
  return (
    <header className="flex flex-wrap items-baseline justify-between gap-3">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="page-title">{detail.name}</h1>
        <span className="text-micro font-semibold text-muted-foreground">
          {detail.status.replace("_", " ")}
        </span>
        {detail.status === "a_terra" ? (
          <span className="inline-flex items-center gap-1 text-micro font-semibold text-muted-foreground">
            <Lock className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
            prezzi bloccati
          </span>
        ) : null}
      </div>
      <span className="font-mono text-data text-muted-foreground">
        {detail.landed_on ? `sbarcato ${formatDate(detail.landed_on)}` : "sbarco non dichiarato"}
      </span>
    </header>
  );
}

interface LinesProps {
  lines: ContainerLineOut[];
  canaliAttivi: string[];
  labelDi: Map<string, string>;
  selected: Set<number>;
  headerCheckbox: React.RefObject<HTMLInputElement>;
  allSelected: boolean;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  onRemove: (id: number) => void;
}

function Lines(props: LinesProps): JSX.Element {
  const { lines, canaliAttivi, labelDi, selected, headerCheckbox, allSelected } = props;
  if (lines.length === 0) {
    return (
      <div className="rounded-[var(--radius-lg)] border border-line bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Nessuna riga. Aggiungi gli articoli che questo container ha portato.
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-line bg-card">
      <table className="w-full text-data">
        <caption className="sr-only">Righe del container e margine per canale</caption>
        <thead>
          <tr className="border-b border-line bg-paper-2 text-left text-micro font-semibold text-muted-foreground">
            <th scope="col" className="px-3 py-2">
              <input
                ref={headerCheckbox}
                type="checkbox"
                checked={allSelected}
                onChange={props.onToggleAll}
                aria-label="Seleziona tutte le righe"
              />
            </th>
            <th scope="col" className="px-3 py-2">SKU</th>
            <th scope="col" className="px-3 py-2">Prodotto</th>
            <th scope="col" className="px-3 py-2 text-right">Pz</th>
            <th scope="col" className="px-3 py-2 text-right">Merce</th>
            <th scope="col" className="px-3 py-2 text-right">Costo</th>
            <th scope="col" className="px-3 py-2 text-right">Logist.</th>
            <th scope="col" className="px-3 py-2 text-right">Sped.</th>
            <th scope="col" className="px-3 py-2 text-right">Prezzo</th>
            {canaliAttivi.map((id) => (
              <th key={id} scope="col" className="px-3 py-2 text-right">
                {labelDi.get(id) ?? id}
              </th>
            ))}
            <th scope="col" className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b border-line transition-colors last:border-0 hover:bg-paper-2">
              <td className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={selected.has(line.id)}
                  onChange={() => props.onToggle(line.id)}
                  aria-label={`Seleziona ${line.sku}`}
                />
              </td>
              <td className="px-3 py-2 font-code">{line.sku}</td>
              <td className="px-3 py-2">
                {line.name ?? <span className="text-muted-foreground">—</span>}
                {line.clone_sku ? (
                  <span className="tag unknown ml-2">
                    + clone BS
                  </span>
                ) : null}
                {!line.in_catalog ? (
                  <span className="tag risk ml-2">
                    non a catalogo
                  </span>
                ) : null}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{line.quantity}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatPrice(line.goods_cost)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatPrice(line.cost)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatPrice(line.packaging_cost)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">
                {formatPrice(line.shipping)}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatPrice(line.target_price)}
              </td>
              {canaliAttivi.map((id) => {
                const m = line.margins[id];
                return (
                  <td
                    key={id}
                    className="px-3 py-2 text-right font-mono tabular-nums"
                    style={{ color: marginTone(m?.margin_percent ?? null) }}
                  >
                    {m ? formatPercent(m.margin_percent) : "—"}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  onClick={() => props.onRemove(line.id)}
                  aria-label={`Togli ${line.sku} dal container`}
                  className="rounded p-1 text-muted-foreground hover:text-foreground"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden="true" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Actions({
  detail,
  onLand,
  onClose,
}: {
  detail: ContainerDetailOut;
  onLand: () => void;
  onClose: (successor: string | null) => void;
}): JSX.Element | null {
  const [successor, setSuccessor] = useState("");
  const altri = useQuery({ queryKey: ["containers"], queryFn: api.containers });
  const candidati = (altri.data ?? []).filter(
    (c) => c.code !== detail.code && c.status === "in_arrivo",
  );

  if (detail.status === "chiuso") return null;

  if (detail.status === "in_arrivo") {
    return (
      <section className="flex flex-col gap-2 rounded-[var(--radius-lg)] border border-line bg-card p-4">
        <h2 className="text-md font-semibold">Dichiara a terra</h2>
        <p className="max-w-[68ch] text-md text-muted-foreground">
          Da qui il container comanda i costi dei suoi articoli e ne blocca il prezzo. Falla
          quando la merce è davvero in magazzino: la data di sbarco fa partire il conto della
          logistica, e anticiparla alzerebbe il floor per un motivo falso.
        </p>
        <button
          type="button"
          onClick={onLand}
          disabled={!detail.landed_on}
          className="btn btn-accent w-fit"
        >
          Dichiara a terra
        </button>
        {!detail.landed_on ? (
          <p className="text-data text-muted-foreground">
            Serve prima una data di sbarco.
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-2 rounded-[var(--radius-lg)] border border-line bg-card p-4">
      <h2 className="text-md font-semibold">Chiudi container</h2>
      <p className="max-w-[68ch] text-md text-muted-foreground">
        La merce è finita. Se un altro container subentra con gli stessi articoli il costo
        passa a lui e il prezzo resta bloccato. Altrimenti gli articoli tornano al listino del
        fornitore: prima si restituisce il costo, poi si sblocca il prezzo — mai al contrario.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-micro font-semibold text-muted-foreground">
            Subentra
          </span>
          <select
            value={successor}
            onChange={(e) => setSuccessor(e.target.value)}
            className="h-9 rounded-[var(--radius)] border border-line-2 bg-card px-2 text-sm"
          >
            <option value="">Nessuno — torna al listino fornitore</option>
            {candidati.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => onClose(successor || null)}
          className="btn btn-outline"
        >
          Chiudi container
        </button>
      </div>
    </section>
  );
}
